import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Projects } from '../../api/Endpoints/project';
import { ProjectUsers } from '../../api/Endpoints/ProjectUsers';
import SelectX from '../Form/SelectX';
import { fetchProjects } from '../CommonFunctions/DropdownLists';

const ProjectUserPriority = ({ open, setOpen }) => {
  const { control, watch, setValue, reset } = useForm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [projectSelected, setProjectSelected] = useState(false);
  const [roundRobin, setRoundRobin] = useState(false);

  const handleClose = () => {
    setOpen(false);
    reset();
    setUsers([]);
    setProjectSelected(false);
  };

  const fetchProjectUsers = async () => {
    const selectedProject = watch('project');
    if (!selectedProject) {
      toast.warning('Please select a project first');
      return;
    }

    setLoading(true);
    try {
      const response = await ProjectUsers.getProjectUsers({ projects_id: selectedProject.id });
      if (response.data && Array.isArray(response.data?.users)) {
        // Map the response data to include priority and pause fields
        const usersWithPriority = response.data?.users.map(user => ({
          ...user,
          priority: user.lead_assign_priority || 0,
          pause: user.round_robin_paused || false
        }));
        setUsers(usersWithPriority);
        setRoundRobin(response.data?.project?.is_round_robin_assign);
        setProjectSelected(true);
      } else {
        setUsers([]);
        toast.error('Failed to fetch project users');
      }
    } catch (error) {
      console.error('Error fetching project users:', error);
      toast.error('Error fetching project users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePriorityChange = (userId, value) => {
    setUsers(prevUsers => 
      prevUsers.map(user => 
        user.id === userId ? { ...user, priority: parseInt(value) || 0 } : user
      )
    );
  };

  const handlePauseToggle = (userId) => {
    setUsers(prevUsers => 
      prevUsers.map(user => 
        user.id === userId ? { ...user, pause: !user.pause } : user
      )
    );
  };

  const handleUpdatePriorities = async () => {
    if (users.length === 0) {
      toast.warning('No users to update');
      return;
    }

    setUpdating(true);
    try {
      // Prepare payload
      const selectedProject = watch('project');
      const payload = {
        project_id: selectedProject?.id,
        round_robin: roundRobin ? 1 : 0,
        data: users.map(user => ({
          id: user.id,
          priority: user.priority,
          pause: user.pause
        }))
      };

      const response = await ProjectUsers.updatePriorities(payload);
      
      if (response.data && response.data.status === 'success') {
        toast.success(response.data.message || 'Priorities updated successfully');
        handleClose();
      } else {
        toast.error(response.data.message || 'Failed to update priorities');
      }
    } catch (error) {
      console.error('Error updating priorities:', error);
      toast.error('Error updating priorities');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Project User Priority Management</DialogTitle>
      
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1,alignItems:'flex-end' }}>
          <Grid item xs={12} sm={8}>
            <SelectX
              options={fetchProjects}
              label="Select Project"
              name="project"
              control={control}
              defaultValue={watch('project')}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button 
              variant="contained" 
              onClick={fetchProjectUsers}
              disabled={loading}
              sx={{ mt: 1}}
            >
              {loading ? <CircularProgress size={24} /> : 'Fetch Assign Users'}
            </Button>
          </Grid>
        </Grid>

        {projectSelected && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                User Priority List
              </Typography>
              <FormControlLabel 
                control={
                  <Switch 
                    checked={roundRobin} 
                    onChange={(e) => setRoundRobin(e.target.checked)} 
                  />
                } 
                label="Round Robin" 
              />
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <CircularProgress />
              </Box>
            ) : users.length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User Name</TableCell>
                      <TableCell>User Role</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Pause</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.user_name}</TableCell>
                        <TableCell>{user.user_role ? user.user_role.charAt(0).toUpperCase() + user.user_role.slice(1) : ''}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={user.priority}
                            onChange={(e) => handlePriorityChange(user.id, e.target.value)}
                            inputProps={{ min: 0 }}
                            variant="outlined"
                            size="small"
                            sx={{ width: '100px' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={user.pause}
                            onChange={() => handlePauseToggle(user.id)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography>No users found for this project</Typography>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        {projectSelected && users.length > 0 && (
          <Button 
            onClick={handleUpdatePriorities} 
            variant="contained" 
            color="primary"
            disabled={updating}
          >
            {updating ? <CircularProgress size={24} /> : 'Update Priorities'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProjectUserPriority;